import { FC } from 'react';
import { useGetStudentCourseQuery } from '@teachur/http-client';
import type { UserCourse } from '@teachur/http-client';
import { StatusBadge } from './StatusBadge';
import { CourseNotes } from './CourseNotes';
import { StudentCourseSelfAssess } from './CourseStepDetails/StudentCourseSelfAssess';
import { StudentCourseExam } from './CourseStepDetails/StudentCourseExam';
import { StudentCourseStats } from './StudentCourseStats';
import { StudentCourseProject } from './CourseStepDetails/StudentCourseProject';
import { getSelfAssessSummary } from '../../utils/selfAssessHelpers';
import { getExamSummary } from '../../utils/examHelpers';
import { getProjectSummary } from '../../utils/projectHelpers';
import { getCourseHearingSummary } from '../../utils/hearingHelpers';
import { getCurrentStudentCourseStep, getCourseStepLabel } from '../../utils/courseStepHelpers';

interface CourseDetailPanelProps {
  userCourse: UserCourse;
  studentId: number;
}

export const CourseDetailPanel: FC<CourseDetailPanelProps> = ({ userCourse, studentId }) => {
  const courseName = userCourse.course?.name ?? '—';
  const courseId = userCourse.courseId ?? userCourse.course?.id;

  const { data: courseStudentProgress } = useGetStudentCourseQuery(courseId, studentId, {
    enabled: !!courseId && !!studentId,
  });

  const resolvedUserCourse = courseStudentProgress?.userCourse ?? userCourse;

  const selfAssessSummary = getSelfAssessSummary(resolvedUserCourse, courseStudentProgress);
  const examSummary = getExamSummary(courseStudentProgress);
  const projectSummary = getProjectSummary(courseStudentProgress);
  const hearingSummary = getCourseHearingSummary(courseStudentProgress);

  const currentStep = getCurrentStudentCourseStep({
    userCourse: resolvedUserCourse,
    selfAssessSummary,
    examSummary,
    projectSummary,
    hearingSummary,
  });

  const currentStepLabel = getCourseStepLabel(currentStep, resolvedUserCourse.passed);

  return (
    <div className="flex flex-col gap-5 rounded-3xl border border-(--neutral-white-NW800) p-5">
      <div className="flex items-center justify-between gap-3">
        <h3 className="[font-family:var(--font-arvo)]! [font-size:var(--fs-h3)] m-0! font-bold text-(--neutral-black-NB1000)!">
          {courseName}
        </h3>

        <StatusBadge userCourse={resolvedUserCourse} />
      </div>

      <div className="flex flex-col gap-3">
        <StudentCourseStats userCourse={resolvedUserCourse} currentStep={currentStepLabel} />
        <CourseNotes notes={courseStudentProgress?.notes ?? userCourse.notes} />
        <StudentCourseSelfAssess summary={selfAssessSummary} />
        <StudentCourseExam summary={examSummary} />
        <StudentCourseProject summary={projectSummary} />
      </div>
    </div>
  );
};
